import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  IAdminDeviceHistoryEntry,
  IAdminPatientDevice,
} from "../../../../@types/admin/AdminDevice";
import { TableSorted } from "../../../../components/TableSorted";
import { useAdminDeviceHistory } from "../../../../hooks/queries/admin/devices";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import {
  ErrorMessage,
  ModalContainer,
  ModalLabel,
  ModalSection,
  ModalTitle,
  ModalValue,
} from "../AdminPage.styles";
import { TableContainer } from "../TableContainer";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";

interface IDeviceHistoryModalProps {
  device: IAdminPatientDevice;
}

/**
 * A device's detail and the patients it has belonged to — Retool's
 * `modalDeviceHistory`.
 *
 * Retool showed device name / id / identifier as three disabled text inputs,
 * which read as an editable form that had been locked. They are plain
 * read-only values here.
 */
export function DeviceHistoryModal({ device }: IDeviceHistoryModalProps) {
  const { deviceHistory, isLoading, isError } = useAdminDeviceHistory(
    device.identifier
  );

  const columns: ColumnDef<IAdminDeviceHistoryEntry, string>[] = useMemo(
    () => [
      { accessorKey: "patientName", header: "Patient", enableSorting: true },
      {
        id: "patientId",
        header: "Patient ID",
        accessorFn: (entry) => String(entry.patientId ?? ""),
        enableSorting: true,
      },
      { accessorKey: "action", header: "Action", enableSorting: true },
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.date, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "performedBy",
        header: "Performed by",
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <ModalContainer>
      <ModalTitle>Device history</ModalTitle>

      <ModalSection>
        <ModalLabel>Device</ModalLabel>
        <ModalValue>
          {deviceHistory?.deviceName ?? device.deviceName}
        </ModalValue>
        <ModalLabel>Identifier</ModalLabel>
        <ModalValue>
          {deviceHistory?.identifier ?? device.identifier}
        </ModalValue>
        <ModalLabel>First used</ModalLabel>
        <ModalValue>
          {getLongFormattedDate(deviceHistory?.firstUsed, "-")}
        </ModalValue>
        <ModalLabel>Last used</ModalLabel>
        <ModalValue>
          {getLongFormattedDate(deviceHistory?.lastUsed, "-")}
        </ModalValue>
      </ModalSection>

      {isError && (
        <ErrorMessage>
          Failed to load this device&apos;s history. Close the dialog and try
          again.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-device-history"]]}>
          <TableSorted
            columns={columns}
            data={deviceHistory?.patientHistory ?? []}
            emptyStateText={
              isLoading ? <TableLoadingState /> : "No history available"
            }
          />
        </TableWrapper>
      </TableContainer>
    </ModalContainer>
  );
}
